import {CommonModule} from '@angular/common';
import {Component, inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';
import {
  DialogCloseButtonComponent,
  PrimaryButtonComponent,
  SectionComponent
} from '@q9elements/ui-kit/components';

import {
  ALREADY_CONNECTED_ERR,
  SfConnectionErrorDialogData,
  SfDetailsItem
} from '../../models/salesforce-orgs.interface';
import {ConnectionDetailsListComponent} from '../connection-details-list/connection-details-list.component';

@Component({
  standalone: true,
  selector: 'q9ma-sf-connection-error',
  imports: [
    CommonModule,
    TranslateModule,
    DialogCloseButtonComponent,
    MatDialogModule,
    SectionComponent,
    MatIconModule,
    PrimaryButtonComponent,
    ConnectionDetailsListComponent
  ],
  templateUrl: './sf-connection-error.component.html',
  styleUrls: ['../sf-connection-info/sf-connection-info-dialog.component.scss']
})
export class SfConnectionErrorComponent implements OnInit {
  public readonly data = inject<SfConnectionErrorDialogData>(MAT_DIALOG_DATA);
  public readonly dialogRef = inject(MatDialogRef<SfConnectionErrorComponent>);
  public readonly errorSignal = signal<string>('Unknown Error');
  public readonly alreadyConnected = signal<boolean>(false);

  public detailsItems: SfDetailsItem[] = [];
  public emailLink =
    'https://mail.google.com/mail/?view=cm&fs=1' +
    '&to=support@elements.cloud' +
    '&su=The%20Salesforce%20Org%20connection%20failed' +
    '&body=Hi%20Elements%20Support%20team%2C%0D%0A%0D%0AI%27m%20having%20trouble%20connecting%20' +
    'to%20my%20Salesforce%20Org.' +
    '%20Here%20are%20the%20details%3A%0D%0A%0D%0A%5Binsert%20screenshot%5D%0D%0A;';

  public ngOnInit(): void {
    this.setDetails();
    this.setErrorMsg();
  }

  public setDetails(): void {
    this.detailsItems = [
      {
        title: 'SF_CONNECTIONS.INTEGRATION_USER',
        icon: 'person',
        value: this.data.username
      },
      {
        title: 'SF_CONNECTIONS.ORG_DOMAIN',
        icon: 'globe',
        value: this.data.orgDomain || ''
      },
      {
        title: 'SF_CONNECTIONS.ORG_ID',
        icon: 'wrench',
        value: this.data.orgId || ''
      },
      {
        title: 'SF_CONNECTIONS.ENVIRONMENT',
        icon: 'database',
        value: this.data.isSandbox ? 'Sandbox' : 'Production'
      }
    ];
  }

  private getPrefix(errorSource: string) {
    switch (errorSource) {
      case 'SF_AUTH':
        return 'Salesforce OAuth 2.0 returned an authentication error:';

      case 'Elements':

      // falls through
      case 'OAUTH':

      // falls through
      default:
        return 'Something went wrong while connecting your Salesforce Org:';
    }
  }

  private getErrorMsg() {
    const {errorData, errorSource} = this.data.error;
    const {error} = errorData;

    const prefix = this.getPrefix(errorSource);
    const inParenthesis =
      errorData.message || errorData.status || errorData.error_description || 'Unknown error';

    switch (errorSource) {
      case 'SF_AUTH':
        return `${prefix} ${error}, ${errorData.error_description}`;

      case 'Elements':

      // falls through
      case 'OAUTH':

      // falls through
      default:
        return `${prefix} ${error} [${inParenthesis}]`;
    }
  }

  public setErrorMsg(): void {
    const {errorData} = this.data.error;
    const {error} = errorData;

    if (error === ALREADY_CONNECTED_ERR) {
      this.alreadyConnected.set(true);

      return;
    }

    this.errorSignal.set(this.getErrorMsg());
  }

  public acknowledge(): void {
    this.dialogRef.close();
  }
}
